import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { modulesAPI } from '../api/client';
import { PAGES } from '../utils/constants';

const ModuleManager = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [pageCodeFilter, setPageCodeFilter] = useState(null);
  const [form] = Form.useForm();

  // 加载模块列表
  const loadModules = async () => {
    setLoading(true);
    try {
      const params = pageCodeFilter ? { page_code: pageCodeFilter } : {};
      const data = await modulesAPI.getAll(params);
      setModules(data);
    } catch (error) {
      message.error('加载模块列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModules();
  }, [pageCodeFilter]);

  // 打开创建/编辑弹窗
  const openModal = (module = null) => {
    setEditingModule(module);
    if (module) {
      form.setFieldsValue(module);
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 关闭弹窗
  const closeModal = () => {
    setModalVisible(false);
    setEditingModule(null);
    form.resetFields();
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingModule) {
        await modulesAPI.update(editingModule.id, values);
        message.success('更新模块成功');
      } else {
        await modulesAPI.create(values);
        message.success('创建模块成功');
      }
      closeModal();
      loadModules();
    } catch (error) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      message.error('操作失败: ' + error.message);
    }
  };

  // 删除模块
  const handleDelete = async (id) => {
    try {
      await modulesAPI.delete(id);
      message.success('删除模块成功');
      loadModules();
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '所属页面',
      dataIndex: 'page_code',
      key: 'page_code',
      width: 150,
      render: (pageCode) => {
        const page = PAGES.find(p => p.code === pageCode);
        return page ? page.name_zh : pageCode;
      }
    },
    {
      title: '模块中文名称',
      dataIndex: 'name_zh',
      key: 'name_zh',
    },
    {
      title: '模块英文名称',
      dataIndex: 'name_en',
      key: 'name_en',
    },
    {
      title: '模块代码',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此模块吗？"
            description="删除模块将同时删除其下所有字段"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Select
            placeholder="筛选页面"
            style={{ width: 200 }}
            allowClear
            value={pageCodeFilter}
            onChange={setPageCodeFilter}
            options={[
              { value: null, label: '全部页面' },
              ...PAGES.map(p => ({ value: p.code, label: p.name_zh }))
            ]}
          />
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
          创建模块
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={modules}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingModule ? '编辑模块' : '创建模块'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={closeModal}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="page_code"
            label="所属页面"
            rules={[{ required: true, message: '请选择所属页面' }]}
          >
            <Select placeholder="请选择页面">
              {PAGES.map(page => (
                <Select.Option key={page.code} value={page.code}>
                  {page.name_zh} ({page.name_en})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name_zh"
            label="模块中文名称"
            rules={[{ required: true, message: '请输入模块中文名称' }]}
          >
            <Input placeholder="例如：招聘信息" />
          </Form.Item>

          <Form.Item
            name="name_en"
            label="模块英文名称"
            rules={[{ required: true, message: '请输入模块英文名称' }]}
          >
            <Input placeholder="例如：Recruitment" />
          </Form.Item>

          <Form.Item
            name="code"
            label="模块代码"
            rules={[
              { required: true, message: '请输入模块代码' },
              { pattern: /^[a-z0-9_-]+$/, message: '只能包含小写字母、数字、下划线和短横线' }
            ]}
          >
            <Input placeholder="例如：recruitment" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModuleManager;







